// Crop+permute mat_mod's (1, 35, s_pad, n_pad) output into per-Gaussian
// albedo[P*3] + f_dec[P*32], and build the spec_mod-shaped f_dec_pad plane
// (32, s_pad, n_pad) with zero padding past (S, N).
//
// Strand-major layout: gaussian i = strand i//N, sample i%N.
// Layout in NCHW Float32Array: data[c*s_pad*n_pad + s*n_pad + n].
//
// TODO(wasm-followup): this is the hottest one-shot load step (large nested
// loop over P*32 channels). Natural first WASM extraction — swap the body of
// `unpackMatMod` for a Rust/wasm-bindgen impl that takes the matUv buffer +
// dims and writes into the (wasm-backed) HairData buffers. Module boundary
// already lets us A/B the JS and WASM versions without touching the
// orchestrator.

const MAT_TOTAL_CH = 35; // albedo (3) + f_dec (32)

// Writes mat_mod output into the caller-owned HairData (`data.albedo`,
// `data.fDec`, `data.fDecPad`); does not allocate. Throws on a dims
// mismatch so a model swap is caught at init rather than producing
// silently-wrong shading.
export function unpackMatMod(matUv, dims, data) {
  const { P, S, N, sPad, nPad, albedo, fDec, fDecPad } = data;
  if (dims.length !== 4 || dims[1] !== MAT_TOTAL_CH ||
      dims[2] !== sPad || dims[3] !== nPad) {
    throw new Error(`[hair] unexpected mat_mod output dims: ${dims}`);
  }
  const plane = sPad * nPad;

  // Albedo: channels 0..2.
  for (let i = 0; i < P; i++) {
    const s = (i / N) | 0;
    const n = i % N;
    const baseIdx = s * nPad + n;
    albedo[i * 3]     = matUv[0 * plane + baseIdx];
    albedo[i * 3 + 1] = matUv[1 * plane + baseIdx];
    albedo[i * 3 + 2] = matUv[2 * plane + baseIdx];
  }
  // f_dec: channels 3..34.
  for (let i = 0; i < P; i++) {
    const s = (i / N) | 0;
    const n = i % N;
    const baseIdx = s * nPad + n;
    for (let c = 0; c < 32; c++) {
      fDec[i * 32 + c] = matUv[(3 + c) * plane + baseIdx];
    }
  }
  // f_dec_pad: (32, sPad, nPad), zero past (S, N). Use raw mat output for
  // active region; zero-fill explicitly past S and N to match Python
  // training-time pad behaviour (`F.pad(crop, zero-pad)`).
  for (let c = 0; c < 32; c++) {
    const srcC = (3 + c) * plane;
    const dstC = c * plane;
    for (let s = 0; s < S; s++) {
      const srcRow = srcC + s * nPad;
      const dstRow = dstC + s * nPad;
      for (let n = 0; n < N; n++) {
        fDecPad[dstRow + n] = matUv[srcRow + n];
      }
    }
  }

  // Per-channel diagnostic stats — surfaces lopsided channels (e.g.
  // R-dominant) that would point at a colour problem rather than a
  // shading one.
  const stats = [0, 1, 2].map((c) => {
    let lo = Infinity, hi = -Infinity, sum = 0, n = 0;
    for (let i = 0; i < P; i++) {
      const v = albedo[i * 3 + c];
      if (v < lo) lo = v; if (v > hi) hi = v; sum += v; n++;
    }
    return { lo, hi, mean: sum / n };
  });
  console.info('[hair] albedo per-channel stats: R=', stats[0], 'G=', stats[1], 'B=', stats[2]);
}
